'use client'

import { Accordion } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { cn } from '@/lib/utils'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCardFacet, TFacet, TFacetValue } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'
import { getActiveFilterSections } from '../../../utils/data-utils'
import { getFilterTagsFromUrl } from '../../../utils/filter-tag-utils'
import { FilterCheckbox } from '../element/filter-checkbox'
import { FilterColors } from '../element/filter-colors'
import { FilterItemAccordion } from '../element/filter-item-accordion'
import { FilterPriceRange } from '../element/filter-price-range'
import { FilterRadio } from '../element/filter-radio'
import { FilterSizes } from '../element/filter-sizes'
import { FilterBarTags } from '../filter-bar-tags'
import { FilterListSkeleton } from './filter-list-skeleton'

type Props = {
  className?: string
  onClose?: () => void
  facets: TFacet[]
  card_facets: TCardFacet[]
  initialFilters?: Record<string, string | string[] | number | undefined>
}

export const FilterList: React.FC<Props> = ({
  className,
  onClose,
  facets,
  card_facets,
}) => {
  const isMobile = useIsMobileOrTabletClient()
  const t = useTranslations('collection_page')

  const {
    filters,
    setFilter,
    clearFilters,
    setConcatValue,
    isInitialized,
    originalSlug,
  } = useCollectionFilterStoreV2(
    useShallow((state) => ({
      filters: state.filters,
      setFilter: state.setFilter,
      clearFilters: state.clearFilters,
      setConcatValue: state.setConcatValue,
      isInitialized: state.isInitialized,
      originalSlug: state.originalSlug,
    }))
  )

  // State để lưu default accordion sections - chỉ set một lần từ serverData
  const [defaultOpenSections, setDefaultOpenSections] = useState<string[]>([])

  // Ref để track xem đã tính toán default accordion chưa
  const hasCalculatedDefault = useRef(false)

  // Tính toán default accordion sections khi store đã initialize
  useEffect(() => {
    // Chỉ chạy một lần sau khi store đã initialize
    if (isInitialized && !hasCalculatedDefault.current) {
      const sections = getActiveFilterSections(facets, filters)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- ref-guarded one-time computation once the filter store initializes
      setDefaultOpenSections(sections)
      hasCalculatedDefault.current = true
    }
  }, [isInitialized, facets, filters])

  // Handle remove tag
  const handleRemoveTag = (tag: {
    parentKey: string
    key: string
    title: string
    value: string
  }) => {
    if (tag.parentKey === 'all') {
      clearFilters()
    } else {
      // Get current filter values
      const currentValue = filters[tag.parentKey]

      // Check if this is an array (multi-value filter)
      if (Array.isArray(currentValue)) {
        // Remove only the specific value from the array
        const newValues = currentValue.filter((v) => v !== tag.value)

        if (newValues.length === 0) {
          // If array is now empty, remove the filter completely
          setFilter(tag.parentKey, undefined)
        } else {
          // Update with remaining values
          setFilter(tag.parentKey, newValues)
        }
      } else {
        // Single value filter - remove completely
        setFilter(tag.parentKey, undefined)
      }
    }

    // Cuộn lên đầu danh sách sản phẩm sau khi xóa filter tag
    scrollToProductSection(200, 50)
  }

  if (!facets || facets.length === 0) {
    return <FilterListSkeleton />
  }

  const renderFacet = (facet: TFacet) => {
    const {
      seo_alias,
      title,
      is_allow_multiple,
      facet_values,
      is_concat_slug,
    } = facet

    const items = facet_values.map((v: TFacetValue) => ({
      label: v.title,
      value: v.seo_alias,
    }))

    // Helper function to handle filter change with concat logic
    const handleFilterChange = (
      key: string,
      value: string | string[] | undefined
    ) => {
      // Get current concatValue from store to check if slug already has concat value
      const currentConcatValue =
        useCollectionFilterStoreV2.getState().concatValue

      // setFilter will handle concatValue logic based on is_allow_multiple
      // Only manually set concatValue if filter doesn't allow multiple or no existing concatValue
      setFilter(key, value)

      // Manual concatValue update only when:
      // 1. Filter is cleared (value is null/undefined/empty)
      // 2. OR filter doesn't allow multiple values AND no existing concatValue
      // Otherwise, let setFilter handle it (store will keep concatValue if is_allow_multiple)
      if (is_concat_slug && !value) {
        // Clear concatValue if filter is cleared
        setConcatValue(null)
      } else if (
        is_concat_slug &&
        value &&
        !is_allow_multiple &&
        !currentConcatValue
      ) {
        // Only set concatValue if filter doesn't allow multiple AND no existing concatValue
        // (If is_allow_multiple and has concatValue, store will keep it)
        const concatVal = Array.isArray(value) ? value[0] : String(value)
        setConcatValue(concatVal)
      }
      // If is_allow_multiple && has concatValue, store logic will keep existing concatValue

      // Cuộn lên đầu danh sách sản phẩm sau khi thay đổi filter
      scrollToProductSection(200, 50)
    }

    switch (seo_alias) {
      case 'color':
        return (
          <FilterColors
            onFilterChange={(value) => {
              handleFilterChange('color', value)
            }}
            value={(filters?.color as string) || ''}
            attribute={facet}
            originalSlug={originalSlug}
            currentFilters={filters}
            facets={facets}
            cardFacets={card_facets}
          />
        )
      case 'size':
        // Normalize size filter to array and lowercase
        const sizeValue = filters?.size
        let sizeArray: string[] = []

        if (Array.isArray(sizeValue)) {
          sizeArray = sizeValue
        } else if (sizeValue) {
          const sizeStr = String(sizeValue)
          // Handle comma-separated values (e.g., "3xl,xs,4xl")
          if (sizeStr.includes(',')) {
            sizeArray = sizeStr.split(',').map((s) => s.trim())
          } else {
            sizeArray = [sizeStr]
          }
        }

        const normalizedSizeArray = sizeArray
          .map((s) => String(s).trim().toLowerCase())
          .filter((s) => s.length > 0)

        return (
          <FilterSizes
            onFilterChange={(value) => handleFilterChange('size', value)}
            defaultValue={normalizedSizeArray}
            sizeList={facet_values.map((v: TFacetValue) => v.title)}
            originalSlug={originalSlug}
            currentFilters={filters}
            facets={facets}
            cardFacets={card_facets}
          />
        )
      case 'price_range':
        return (
          <FilterPriceRange
            onFilterChange={(values: string[]) =>
              handleFilterChange('price_range', values)
            }
            items={items}
            value={(filters?.price_range as string[]) || []}
            originalSlug={originalSlug}
            currentFilters={filters}
            facets={facets}
            cardFacets={card_facets}
          />
        )
      default:
        if (is_allow_multiple) {
          // Normalize checkbox filter to array and lowercase (handle comma-separated values)
          const value = filters?.[seo_alias]
          let selectedArray: string[] = []

          if (Array.isArray(value)) {
            selectedArray = value
          } else if (value) {
            const valueStr = String(value)
            // Handle comma-separated values (e.g., "value1,value2,value3")
            if (valueStr.includes(',')) {
              selectedArray = valueStr.split(',').map((s) => s.trim())
            } else {
              selectedArray = [valueStr]
            }
          }

          const normalizedSelectedArray = selectedArray
            .map((s) => String(s).trim().toLowerCase())
            .filter((s) => s.length > 0)

          return (
            <FilterCheckbox
              attribute={{
                seo_alias,
                title,
                items: facet_values,
                is_allow_multiple,
                is_concat_slug,
              }}
              onFilterChange={(value) => handleFilterChange(seo_alias, value)}
              selectedValues={normalizedSelectedArray}
              originalSlug={originalSlug}
              currentFilters={filters}
              facets={facets}
              cardFacets={card_facets}
            />
          )
        }
        return (
          <FilterRadio
            attribute={{
              seo_alias,
              title,
              items: facet_values,
              is_allow_multiple,
              is_concat_slug,
            }}
            onFilterChange={(value) => handleFilterChange(seo_alias, value)}
            value={(filters?.[seo_alias] as string) || ''}
            originalSlug={originalSlug}
            currentFilters={filters}
            facets={facets}
            cardFacets={card_facets}
          />
        )
    }
  }

  return (
    <div
      className={cn(
        'relative flex flex-col gap-6 overflow-y-auto overflow-x-hidden pb-36 pr-2 max-lg:max-h-[calc(100vh-50px)]',
        className
      )}
    >
      {isMobile && (
        <FilterBarTags
          tags={(() => {
            const currentUrl =
              typeof window !== 'undefined'
                ? new URL(window.location.href)
                : null
            const originalSlug =
              useCollectionFilterStoreV2.getState().originalSlug
            return currentUrl && originalSlug
              ? getFilterTagsFromUrl(
                  currentUrl,
                  originalSlug,
                  facets,
                  card_facets
                )
              : []
          })()}
          onRemoveTag={handleRemoveTag}
        />
      )}

      <Accordion
        key={`accordion-${defaultOpenSections.join('-')}`}
        type='multiple'
        className='flex w-full flex-col gap-4.5 lg:gap-2'
        defaultValue={defaultOpenSections}
      >
        {facets.map((facet) => (
          <FilterItemAccordion
            key={facet.seo_alias}
            title={facet.title}
            value={facet.seo_alias}
          >
            {renderFacet(facet)}
          </FilterItemAccordion>
        ))}
      </Accordion>
      {isMobile && (
        <div className='fixed bottom-0 left-0 right-0 mt-4 border-t border-t-neutral-200 bg-light p-4'>
          <Button
            variant={'black'}
            className='w-full'
            onClick={() => onClose?.()}
          >
            {t('button.apply')}
          </Button>
        </div>
      )}
    </div>
  )
}
